import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { UploadFile } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { updatePageState } from "../redux/slicePage";
import { RootState } from "../redux/store";
import {
  applyExtraction,
  setParsedFile,
  setRegex,
  setSelectedColumn,
} from "../redux/sliceExtract";
import MuTakoz from "../components/mutakoz";

export default function Main() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const extract = useSelector((state: RootState) => state.extract);
  const [error, setError] = useState("");

  useEffect(() => {
    dispatch(
      updatePageState({
        navItems: [
          { icon: "Info", link: "./about" },
          { icon: "List", link: "./result" },
        ],
        title: "",
      })
    );
  }, [dispatch]);

  const handleFile = async (file: File) => {
    setError("");
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        defval: "",
      }) as unknown[][];

      if (matrix.length === 0) {
        setError("The file appears to be empty.");
        return;
      }

      const headers = (matrix[0] as unknown[]).map((h, i) =>
        String(h ?? "").trim() === "" ? `Column ${i + 1}` : String(h)
      );
      const rows = matrix
        .slice(1)
        .map((row) => (row as unknown[]).map((cell) => String(cell ?? "")));

      dispatch(setParsedFile({ fileName: file.name, headers, rows }));
    } catch {
      setError("Could not read this file. Please try a valid xlsx, xls or csv.");
    }
  };

  const hasData = extract.headers.length > 0;

  return (
    <div>
      <Button
        component="label"
        variant="outlined"
        className="w-full"
        startIcon={<UploadFile />}
      >
        {extract.fileName || "Upload xlsx, xls or csv"}
        <input
          type="file"
          hidden
          accept=".xlsx,.xls,.csv"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFile(file);
            }
            e.target.value = "";
          }}
        />
      </Button>

      {error && (
        <Typography color="error" className="pt-2" variant="body2">
          {error}
        </Typography>
      )}

      <MuTakoz />

      <FormControl fullWidth disabled={!hasData}>
        <InputLabel id="column-select-label">Column</InputLabel>
        <Select
          labelId="column-select-label"
          label="Column"
          value={hasData ? extract.selectedColumn : ""}
          onChange={(e) => {
            dispatch(setSelectedColumn(Number(e.target.value)));
          }}
        >
          {extract.headers.map((header, ix) => (
            <MenuItem key={`col_${ix}`} value={ix}>
              {header}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <MuTakoz />

      <Autocomplete
        freeSolo
        options={extract.savedRegexes}
        value={extract.regex}
        onChange={(_, value) => {
          dispatch(setRegex(value ?? ""));
        }}
        onInputChange={(_, value) => {
          dispatch(setRegex(value ?? ""));
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Regex"
            variant="outlined"
            autoComplete="off"
          />
        )}
      />

      <MuTakoz height={"3rem"} />

      <Button
        variant="contained"
        className="w-full"
        disabled={!hasData}
        onClick={() => {
          dispatch(applyExtraction());
          navigate("./result");
        }}
      >
        Apply
      </Button>
    </div>
  );
}